import React, { useState, useEffect, useCallback } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { postApi, fetchApi } from "redux/action/api_tbeams";
import { Typeahead } from "react-bootstrap-typeahead";
import { getDistinctObjects } from "./CapitalProject";
import {
  Card, Spinner, Table, Col, Row, Label, Input, Button, FormGroup,
} from "reactstrap";
import { formatNumber } from "redux/action/api";
import { useSelector } from "react-redux";
import { useToasts } from "helper/ToastAdapter";
import moment from "moment";

export default function RecallRelease() {
  const { addToast } = useToasts();
  const user = useSelector((e) => e.auth.user);

  const [searchNo, setSearchNo] = useState("");
  const [releaseData, setReleaseData] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searched, setSearched] = useState(false);

  const [economicCode, setEconomicCode] = useState([]);
  const [distinctAdmins, setDistinctAdmins] = useState([]);
  const [filteredEconomicCode, setFilteredEconomicCode] = useState([]);
  const [fetching, setFetching] = useState(false);

  const getList = useCallback(() => {
    setFetching(true);
    fetchApi("get-budget-summary?query_type=recurrent")
      .then((d) => {
        setFetching(false);
        if (d.result) {
          setEconomicCode(d.result);
          const admins = getDistinctObjects(
            d.result,
            "admin_code",
            "admin_description"
          );
          setDistinctAdmins(admins);
        }
      })
      .catch(() => setFetching(false));
  }, []);

  useEffect(() => {
    getList();
  }, [getList]);

  useEffect(() => {
    if (formData?.mda_code && economicCode.length) {
      const filtered = economicCode.filter(
        (item) =>
          String(item.admin_code) === String(formData.mda_code) ||
          String(item.mda_code) === String(formData.mda_code)
      );
      setFilteredEconomicCode(filtered);
    } else {
      setFilteredEconomicCode([]);
    }
  }, [formData?.mda_code, economicCode]);

  const handleSearch = async () => {
    if (!searchNo.trim()) {
      addToast("Please enter an approval number", { appearance: "error" });
      return;
    }
    setLoading(true);
    setSearched(false);
    try {
      const resp = await postApi("recall-release", {
        query_type: "search_by_approval_no",
        approval_no: searchNo.trim(),
      });
      if (resp.success && resp.data && resp.data.length) {
        const data = resp.data;
        setReleaseData(data.map((item) => ({
          ...item,
          amount: Number(item.amount || 0),
          old_amount: Number(item.amount || 0),
        })));
        setFormData({
          collection_date: data[0].collection_date || moment().format("YYYY-MM-DD"),
          approval_date: data[0].approval_date || moment().format("YYYY-MM-DD"),
          mda_name: data[0].mda_name || "",
          mda_code: data[0].mda_code || "",
          description_code: data[0].description_code || "",
          economic_code: data[0].economic_code || "",
          program_code: data[0].program_code || "",
          approved_by: data[0].approved_by || "HE",
          cc: data[0].cc || "",
          narration: data[0].narration || "",
          project_type: data[0].project_type || "",
          payment_type: data[0].payment_type || "",
          mda_head: data[0].mda_head || "",
          send_to_office: data[0].send_to_office || "",
          send_to_mda: data[0].send_to_mda || "",
          approval_type: data[0].approval_type || "",
        });
        setSearched(true);
      } else {
        setReleaseData([]);
        setFormData(null);
        setSearched(true);
        addToast("No records found for this approval number", {
          appearance: "warning",
        });
      }
    } catch (err) {
      addToast("Error searching for release", { appearance: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAmountChange = (idx, value) => {
    const updated = [...releaseData];
    updated[idx] = { ...updated[idx], amount: Number(value) };
    setReleaseData(updated);
  };

  const handleMdaChange = (selected) => {
    if (selected?.length) {
      const item = selected[0];
      setFormData((prev) => ({
        ...prev,
        mda_code: item.admin_code,
        mda_name: item.admin_description,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        mda_code: "",
        mda_name: "",
      }));
    }
  };

  const handleMdaHeadChange = (selected) => {
    if (selected?.length) {
      setFormData((prev) => ({
        ...prev,
        mda_head: selected[0].admin_description,
      }));
    } else {
      setFormData((prev) => ({ ...prev, mda_head: "" }));
    }
  };

  const handleEconomicCodeChange = (selected) => {
    if (selected?.length) {
      const item = selected[0];
      setFormData((prev) => ({
        ...prev,
        economic_code: item.economic_code,
        description_code: item.economic_description,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        economic_code: "",
        description_code: "",
      }));
    }
  };

  const handleCcChange = (selected) => {
    if (selected?.length) {
      setFormData((prev) => ({
        ...prev,
        cc: selected[0].admin_description,
      }));
    } else {
      setFormData((prev) => ({ ...prev, cc: "" }));
    }
  };

  const handleSendToMdaChange = (selected) => {
    if (selected?.length) {
      const item = selected[0];
      setFormData((prev) => ({
        ...prev,
        send_to_mda: item.admin_code,
      }));
    } else {
      setFormData((prev) => ({ ...prev, send_to_mda: "" }));
    }
  };

  const handleSave = async () => {
    if (!formData?.narration?.trim()) {
      addToast("Narration is required", { appearance: "error" });
      return;
    }
    setSaving(true);
    try {
      const items = releaseData.map((item) => ({
        id: item.id,
        amount: item.amount,
        old_amount: item.old_amount,
        ...formData,
      }));

      const resp = await postApi("recall-release", {
        query_type: "save_recall",
        approval_no: searchNo.trim(),
        items,
        userId: user?.id || "",
        username: user?.username || user?.name || "",
      });

      if (resp.success) {
        addToast(resp.message || "Release updated successfully", {
          appearance: "success",
        });
        setReleaseData([]);
        setFormData(null);
        setSearchNo("");
        setSearched(false);
      } else {
        addToast(resp.error || "Failed to save", { appearance: "error" });
      }
    } catch (err) {
      addToast("Error saving recall", { appearance: "error" });
    } finally {
      setSaving(false);
    }
  };

  const amountChanged = releaseData.some(
    (item) => Number(item.amount) !== Number(item.old_amount)
  );
  const totalOld = releaseData.reduce((s, i) => s + Number(i.old_amount || 0), 0);
  const totalNew = releaseData.reduce((s, i) => s + Number(i.amount || 0), 0);

  const selectedMda = formData?.mda_code && distinctAdmins.length
    ? distinctAdmins.filter(
        (a) => String(a.admin_code) === String(formData.mda_code)
      )
    : [];

  const selectedMdaHead = formData?.mda_head && distinctAdmins.length
    ? distinctAdmins.filter(
        (a) => String(a.admin_description) === String(formData.mda_head)
      )
    : [];

  const selectedEcon = formData?.economic_code && filteredEconomicCode.length
    ? filteredEconomicCode.filter(
        (e) => String(e.economic_code) === String(formData.economic_code)
      )
    : [];

  const selectedCc = formData?.cc && distinctAdmins.length
    ? distinctAdmins.filter(
        (a) => String(a.admin_description) === String(formData.cc)
      )
    : [];

  const selectedSendToMda = formData?.send_to_mda && distinctAdmins.length
    ? distinctAdmins.filter(
        (a) => String(a.admin_code) === String(formData.send_to_mda)
      )
    : [];

  const econOptions = getDistinctObjects(
    filteredEconomicCode.filter(
      (item) =>
        item.economic_code &&
        item.economic_description &&
        String(item.economic_code).trim() !== "" &&
        String(item.economic_description).trim() !== ""
    ),
    "economic_code",
    "economic_description"
  );

  return (
    <DashboardLayout>
      <MDBox pt={3} pb={2}>
        <Card style={{ padding: "24px" }}>
          <MDTypography variant="h5" fontWeight="medium" gutterBottom>
            Recall Release
          </MDTypography>

          {/* Search */}
          <Row style={{ alignItems: "flex-end", marginBottom: 20 }}>
            <Col md={5}>
              <FormGroup>
                <Label>Approval Number</Label>
                <Input
                  placeholder="Enter approval number"
                  value={searchNo}
                  onChange={(e) => setSearchNo(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <Button color="primary" onClick={handleSearch} disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Search"}
              </Button>
            </Col>
          </Row>

          {loading && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spinner />
            </div>
          )}

          {!loading && searched && !formData && (
            <MDTypography color="textSecondary">
              No records found for approval number: {searchNo}
            </MDTypography>
          )}

          {/* Form */}
          {formData && (
            <>
              <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                Release Details — {searchNo}
                {releaseData.length > 1 && (
                  <MDTypography
                    variant="caption"
                    color="info"
                    style={{ marginLeft: 12 }}
                  >
                    {releaseData.length} items found
                  </MDTypography>
                )}
              </MDTypography>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label>Collection Date</Label>
                    <Input
                      type="date"
                      value={formData.collection_date ?? ""}
                      onChange={(e) =>
                        handleFormChange("collection_date", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Approval Date</Label>
                    <Input
                      type="date"
                      value={formData.approval_date ?? ""}
                      onChange={(e) =>
                        handleFormChange("approval_date", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Approved By</Label>
                    <Input
                      value={formData.approved_by ?? ""}
                      onChange={(e) =>
                        handleFormChange("approved_by", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontWeight: 600, color: "#495057" }}>
                      Select MDA to be charged
                    </Label>
                    {fetching && <Spinner size="sm" />}
                    <Typeahead
                      size="sm"
                      id="recall-mda"
                      onChange={handleMdaChange}
                      options={distinctAdmins}
                      labelKey={(option) => `${option.admin_description}`}
                      placeholder="Select MDA"
                      selected={selectedMda}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>MDA Code</Label>
                    <Input value={formData.mda_code ?? ""} disabled />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Program Code</Label>
                    <Input
                      value={formData.program_code ?? ""}
                      onChange={(e) =>
                        handleFormChange("program_code", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={5}>
                  <FormGroup>
                    <Label style={{ fontWeight: 600, color: "#495057" }}>
                      Select Economic Code
                    </Label>
                    <Typeahead
                      size="sm"
                      id="recall-economic"
                      onChange={handleEconomicCodeChange}
                      options={econOptions}
                      labelKey={(option) =>
                        `${option.economic_description} - ${option.economic_code}`
                      }
                      placeholder="Select Economic Code"
                      selected={selectedEcon}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>Description Code</Label>
                    <Input
                      value={formData.description_code ?? ""}
                      onChange={(e) =>
                        handleFormChange("description_code", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Approval Type</Label>
                    <Input
                      value={formData.approval_type ?? ""}
                      onChange={(e) =>
                        handleFormChange("approval_type", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontWeight: 600, color: "#495057" }}>
                      Copy to (CC)
                    </Label>
                    <Typeahead
                      size="sm"
                      id="recall-cc"
                      onChange={handleCcChange}
                      options={distinctAdmins}
                      labelKey={(option) => `${option.admin_description}`}
                      placeholder="Select MDA for CC"
                      selected={selectedCc}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontWeight: 600, color: "#495057" }}>
                      Select MDA Payable to
                    </Label>
                    <Typeahead
                      size="sm"
                      id="recall-head"
                      onChange={handleMdaHeadChange}
                      options={distinctAdmins}
                      labelKey={(option) =>
                        `${option.admin_description}/${option.admin_code}`
                      }
                      placeholder="Select Head"
                      selected={selectedMdaHead}
                    />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label>MDA Head (text)</Label>
                    <Input
                      value={formData.mda_head ?? ""}
                      onChange={(e) =>
                        handleFormChange("mda_head", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Project Type</Label>
                    <Input
                      value={formData.project_type ?? ""}
                      onChange={(e) =>
                        handleFormChange("project_type", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Payment Type</Label>
                    <Input
                      value={formData.payment_type ?? ""}
                      onChange={(e) =>
                        handleFormChange("payment_type", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={{ fontWeight: 600, color: "#495057" }}>
                      Send to MDA
                    </Label>
                    <Typeahead
                      size="sm"
                      id="recall-send-mda"
                      onChange={handleSendToMdaChange}
                      options={distinctAdmins}
                      labelKey={(option) =>
                        `${option.admin_code} - ${option.admin_description}`
                      }
                      placeholder="Select MDA"
                      selected={selectedSendToMda}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Send To Office</Label>
                    <Input
                      value={formData.send_to_office ?? ""}
                      onChange={(e) =>
                        handleFormChange("send_to_office", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Amounts Table */}
              <MDBox mt={2} mb={2}>
                <MDTypography variant="subtitle2" fontWeight="medium">
                  Amounts
                </MDTypography>
                <Table bordered striped responsive size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>MDA Code</th>
                      <th>Economic Code</th>
                      <th>Old Amount</th>
                      <th>New Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {releaseData.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td>{idx + 1}</td>
                        <td>{item.mda_code || formData.mda_code}</td>
                        <td>{item.economic_code || formData.economic_code}</td>
                        <td style={{ textAlign: "right" }}>
                          {formatNumber(item.old_amount)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <Input
                            type="number"
                            bsSize="sm"
                            style={{ width: 150, textAlign: "right" }}
                            value={item.amount}
                            onChange={(e) =>
                              handleAmountChange(idx, e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td
                        colSpan={3}
                        style={{ textAlign: "right", fontWeight: "bold" }}
                      >
                        Total
                      </td>
                      <td style={{ textAlign: "right", fontWeight: "bold" }}>
                        {formatNumber(totalOld)}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: "bold" }}>
                        {formatNumber(totalNew)}
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </MDBox>

              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>Narration</Label>
                    <Input
                      type="textarea"
                      rows={3}
                      value={formData.narration ?? ""}
                      onChange={(e) =>
                        handleFormChange("narration", e.target.value)
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>

              {amountChanged && (
                <MDBox mb={2}>
                  <MDTypography variant="caption" color="info">
                    Amount changes will create new records with status &quot;Approval
                    Initiated&quot; and mark old records as &quot;Recalled release&quot;.
                    Double-entry budget adjustments will be recorded.
                  </MDTypography>
                </MDBox>
              )}

              <Row>
                <Col md={12} style={{ textAlign: "right" }}>
                  <Button
                    color="success"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? <Spinner size="sm" /> : "Save Changes"}
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
